<template>
  <div>
    <b-container class="wide">
      <b-row class="no-margin-left ml-3 mt-5">
        <b-button
            variant="outline-primary"
            @click="goBack"
        >
          <b-icon icon="arrow-left" class="mr-2" />
          {{ $t('links.admin.go_back') }}
        </b-button>
      </b-row>
      <b-row class="no-margin-left" v-if="shelfTypeAvailability">
        <b-col cols="12" md="6" lg="5" class="mt-4">
          <v-calendar
            is-expanded
            :min-page="minPage"
            :min-date="minDate"
            :max-page="maxPage"
            :max-date="maxDate"
            :disabled-dates="disabledDates"
            color="green"
            :locale="$_languageCode"
            @dayclick="constructBookingInfo"
            @update:from-page="onCalendarPageChange"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mt-4">
          <booking-summary-card
            :bookings="bookingsInfo"
            :market="market"
            :newBooking="bookingInfo"
            :isCustomerView="isCustomerBooking"
            @confirm="confirmBookings"
            @addMore="saveAndAddMoreBookings"
            @deleteNewBooking="deletePendingBooking"
            @deleteBooking="handleDeleteBooking"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>

  import { mapState } from 'vuex'
  import moment from 'moment'
  import BookingPeriodLabelMixin from "@base/mixins/BookingPeriodLabelMixin.vue";
  import Formatting from "@base/mixins/Formatting.vue";
  import TableUtils from "@base/mixins/TableUtils.vue";
  import BookingSummaryCard from "@base/components/booking/BookingSummaryCard.vue";
  import DiscountUtilsMixin from "@base/mixins/DiscountUtilsMixin.vue";
  import {BOOKING_FUTURE_MONTHS_MAX, BOOKING_REQUEST_NEW_TYPE} from "@base/configs";

  export default {
    name: 'SelectStartDateComponent',

    components: {
      BookingSummaryCard,
    },

    mixins: [BookingPeriodLabelMixin, Formatting, TableUtils, DiscountUtilsMixin ],

    props: {
      selectedShelfBookingPrice: {
        type: Object,
        required: true
      },
      market: {
        type: Object,
        required: true
      },
      selectedServices: {
        type: Array,
        required: true
      },
      isCustomerBooking: {
        type: Boolean,
        default: false
      },
    },

    data () {
      return {
        loading: false,
        closingDays: [],
        calendarModelConfig: {
          type: 'string',
          mask: 'YYYY-MM-DD',
        },
        bookingsInfo: [],
        selectedStartDate: null,
        bookingInfo: null,
        cartToken: null,
        currentPage: null,
        fetchedMonths: [],
        mergedAvailability: {
          unavailableDates: [],
          specialClosingDates: [],
          closingDates: [],
          endDates: {},
        },
        minDate: moment().format('YYYY-MM-DD'),
        minPage: {
          year: moment().year(),
          month: moment().month() + 1,
        },
      }
    },

    async created () {
      this.loading = true
      this.loader = this.$loading.show(this.$_loaderOptions)
      this.getBookingsInfo()
      this.getCartToken()
      await this.reloadAvailability()
      this.loader.hide()
      this.loading = false
    },

    computed: {
      ...mapState(['shelfTypeAvailability']),

      futureMonthsAllowed () {
        return Boolean(this.market && this.market.allowFutureMonthsBooking)
      },

      maxMonthsAhead () {
        return this.futureMonthsAllowed ? BOOKING_FUTURE_MONTHS_MAX : 1
      },

      maxDate () {
        return moment().add(this.maxMonthsAhead, 'months').endOf('month').format('YYYY-MM-DD')
      },

      maxPage () {
        const end = moment().add(this.maxMonthsAhead, 'months').endOf('month')
        return {
          year: end.year(),
          month: end.month() + 1,
        }
      },

      shelfAvailabilityPayload () {
        return {
          shelfPeriodPrice: this.selectedShelfBookingPrice ? this.selectedShelfBookingPrice.id : null,
          dateStart: moment().format('YYYY-MM-DD'),
          dateEnd: moment().add(1, 'months').endOf('month').format('YYYY-MM-DD')
        }
      },

      disabledDates () {
        return this.mergedAvailability.unavailableDates.concat(
          this.mergedAvailability.specialClosingDates, this.mergedAvailability.closingDates
        )
      },

    },

    methods: {
      getShelfTypeAvailability (payload = null) {
        return new Promise((resolve) => {
          const action = (this.isCustomerBooking) ? this.$_actionTypes.GET_SHELF_TYPE_AVAILABILITY :
          this.$_actionTypes.GET_SHELF_TYPE_AVAILABILITY_ANON
          this.$store.dispatch(action, payload || this.shelfAvailabilityPayload)
            .then(() => {
              this.mergeAvailability()
              resolve(true)
            })
            .catch((error) => {
              this.$_notifyError(error, this)
              resolve(false)
            })
        })
      },

      mergeAvailability () {
        if (!this.shelfTypeAvailability)
          return
        for (const key of ['unavailableDates', 'specialClosingDates', 'closingDates']) {
          this.mergedAvailability[key] = [...new Set([
            ...this.mergedAvailability[key],
            ...(this.shelfTypeAvailability[key] || []),
          ])]
        }
        this.mergedAvailability.endDates = { ...this.mergedAvailability.endDates, ...(this.shelfTypeAvailability.endDates || {}) }
      },

      async reloadAvailability () {
        this.fetchedMonths = []
        this.mergedAvailability = {
          unavailableDates: [],
          specialClosingDates: [],
          closingDates: [],
          endDates: {},
        }
        await this.getShelfTypeAvailability()
        this.fetchedMonths = [
          moment().format('YYYY-MM'),
          moment().add(1, 'months').format('YYYY-MM'),
        ]
        await this.ensureMonthFetched(this.currentPage)
      },

      onCalendarPageChange (page) {
        this.currentPage = page
        this.ensureMonthFetched(page)
      },

      async ensureMonthFetched (page) {
        if (!page)
          return
        const monthStart = moment({ year: page.year, month: page.month - 1, day: 1 })
        const key = monthStart.format('YYYY-MM')
        if (this.fetchedMonths.includes(key))
          return
        const loader = this.$loading.show(this.$_loaderOptions)
        const fetched = await this.getShelfTypeAvailability({
          shelfPeriodPrice: this.selectedShelfBookingPrice ? this.selectedShelfBookingPrice.id : null,
          dateStart: moment.max(moment(), monthStart).format('YYYY-MM-DD'),
          dateEnd: monthStart.clone().endOf('month').format('YYYY-MM-DD'),
        })
        loader.hide()
        if (fetched) {
          this.fetchedMonths.push(key)
        }
      },

      constructBookingInfo (day) {
        if (day.isDisabled)
          return
        const dateStart = moment(day.date).format('YYYY-MM-DD')
        // The server computes the end, special closings included.
        const end = this.mergedAvailability.endDates[dateStart]
        if (!end)
          return
        this.selectedStartDate = day.date

        this.bookingInfo = {
          "type": BOOKING_REQUEST_NEW_TYPE,
          "dateStartCustomer": moment(this.selectedStartDate).format('YYYY-MM-DD'),
          "shelfTypeName": this.selectedShelfBookingPrice.shelfType.name,
          "bookingPeriodLabel": this.bookingPeriodLabel(this.selectedShelfBookingPrice.bookingPeriod, false),
          "shelfPeriodPrice": this.selectedShelfBookingPrice.id,
          "discountPercent": this.selectedShelfBookingPrice.discountPercent,
          "discountedPrice": this.computeDiscountedPrice(
              this.selectedShelfBookingPrice.price,
              this.selectedShelfBookingPrice.discountPercent,
              this.selectedShelfBookingPrice.discountValidFrom,
              this.selectedShelfBookingPrice.discountValidTo,
          ) || this.selectedShelfBookingPrice.price,
          "isDiscountActive": this.isDiscountActive(
              this.selectedShelfBookingPrice.discountPercent,
              this.selectedShelfBookingPrice.discountValidFrom,
              this.selectedShelfBookingPrice.discountValidTo),
          "originalPrice": this.selectedShelfBookingPrice.price,
          "dateEndCustomer": end.dateEnd,
          "eligibleClosingDays": end.eligibleClosingDays,
          "expiresOn": moment().add(15, 'minutes').toDate().getTime(),
          "cartToken": this.cartToken,
          "bookingAddons": this.selectedServices
        }
      },
      goBack () {
        this.$emit('bookingDateGoBack')
      },

      deletePendingBooking () {
        this.bookingInfo = null
        this.selectedStartDate = null
      },

      async confirmBookings () {
        await this.confirmBookingReservation()
        this.$emit('confirmBookings', this.bookingsInfo)
      },

      async saveAndAddMoreBookings () {
        await this.confirmBookingReservation()
        this.$emit('addMoreBookings', this.bookingsInfo)
      },

      async handleDeleteBooking (bookingInfo) {
        await this.deleteBooking(bookingInfo)
        this.$emit('deleteBooking', this.bookingsInfo)
      },

      confirmBookingReservation() {
        if (!this.bookingInfo)
          return
        return new Promise((resolve) => {
          this.reserveBooking(this.bookingInfo)
            .then(() => {
              localStorage.setItem("bookingsInfo", JSON.stringify(this.bookingsInfo))
              this.bookingInfo = null
              this.$notify({
                group: 'app',
                type: 'success',
                title: this.$t('notifications.title.success'),
                text: this.$t('notifications.text.create_booking.success_booking_added')
              })
              resolve()
            })
        })
      },

      reserveBooking (bookingInfo) {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          const action = (this.isCustomerBooking) ? this.$_actionTypes.BOOKING_REQUEST_RESERVE :
          this.$_actionTypes.BOOKING_REQUEST_RESERVE_ANON
          this.$store.dispatch(action, bookingInfo)
            .then((data) => {
                bookingInfo["bookingRequest"] = data.id
                bookingInfo["cartToken"] = data.cartToken
                bookingInfo["reservedAt"] = data.reservedTs
                this.bookingsInfo.push(bookingInfo)
                resolve()
            })
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
            })
        })
      },

      deleteBooking (bookingInfo) {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          const action = (this.isCustomerBooking) ? this.$_actionTypes.BOOKING_REQUEST_DELETE :
          this.$_actionTypes.BOOKING_REQUEST_DELETE_ANON
          const payload = this.isCustomerBooking
            ? bookingInfo.bookingRequest
            : { id: bookingInfo.bookingRequest, cartToken: bookingInfo.cartToken }
          this.$store.dispatch(action, payload)
            .then(async () => {
              const oldBooking = this.bookingsInfo.findIndex(
                obj => obj.bookingRequest === bookingInfo.bookingRequest
              )
              this.bookingsInfo.splice(oldBooking, 1)
              localStorage.setItem("bookingsInfo", JSON.stringify(this.bookingsInfo))
              await this.reloadAvailability()
              this.$notify({
                group: 'app',
                type: 'success',
                title: this.$t('notifications.title.success'),
                text: this.$t('notifications.text.create_booking.success_booking_deleted')
              })
              resolve()
            })
            .catch(async (error) => {
              if (error.status === 404) {
                const oldBooking = this.bookingsInfo.findIndex(
                obj => obj.bookingRequest === bookingInfo.bookingRequest
              )
              this.bookingsInfo.splice(oldBooking, 1)
              localStorage.setItem("bookingsInfo", JSON.stringify(this.bookingsInfo))
              await this.reloadAvailability()
              this.$notify({
                group: 'app',
                type: 'success',
                title: this.$t('notifications.title.success'),
                text: this.$t('notifications.text.create_booking.success_booking_deleted')
              })
                resolve()
              } else {
                this.$_notifyError(error, this)
              }
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
            })
        })
      },

      getBookingsInfo () {
        this.bookingsInfo = []
        const storageValue = localStorage.getItem("bookingsInfo")
        if (!storageValue) {
          return []
        }
       const bookingsInfo = JSON.parse(localStorage.getItem("bookingsInfo"))
        const now = new Date()
        for (const obj of bookingsInfo) {
          // compare the expiry time of the item with the current time
          if (now.getTime() < obj.expiresOn) {
            // If the item is expired, delete the item from storage
            // and return null
            this.bookingsInfo.push(obj)
          }
        }
        localStorage.setItem("bookingsInfo", JSON.stringify(this.bookingsInfo))
      },

      getCartToken () {
        const existingToken = localStorage.getItem('cartToken');
        this.cartToken = existingToken || crypto.randomUUID();
        localStorage.setItem('cartToken', this.cartToken);
      },
    }
  }

</script>

<style scoped>

</style>
